if (!customElements.get('etsy-circle-monogram-customizer')) {
  class CircleMonogramCustomizer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.initialize();
    }

    async initialize() {
      this.textInput = this.querySelector('.circle-monogram__input');
      this.characterCount = this.querySelector('.ice-customizer__character-count');
      this.enteredTextProperty = this.querySelector('.circle-monogram__entered-text-property');
      this.error = this.querySelector('.ice-customizer__error');
      this.viewer = this.querySelector('.ice-customizer__viewer');
      this.canvas = this.querySelector('.ice-customizer__canvas');
      this.dragHint = this.querySelector('.ice-customizer__drag-hint');
      this.viewControls = this.querySelector('.ice-customizer__view-controls');
      this.viewButtons = [...this.querySelectorAll('[data-ice-view]')];
      this.previewFileInput = this.querySelector('.ice-customizer__preview-file');
      this.previewAttachedProperty = this.querySelector('.ice-customizer__preview-attached-property');
      this.productForm = this.resolveProductForm();

      this.yaw = Math.PI / 4;
      this.pitch = -0.08;
      this.cameraView = '3d';
      this.dragStart = null;
      this.currentInitials = '';
      this.currentVectorSvg = '';
      this.updateTimer = null;
      this.allowPreviewSubmit = false;
      this.submittingForm = null;

      if (this.productForm) {
        this.productForm.enctype = 'multipart/form-data';
        let productFormId = this.productForm.getAttribute('id');
        if (!productFormId) {
          productFormId = `circle-monogram-product-form-${Date.now()}`;
          this.productForm.setAttribute('id', productFormId);
        }
        this.querySelectorAll('[name^="properties["]').forEach((field) => {
          field.setAttribute('form', productFormId);
        });
      }

      this.bindEvents();
      this.updateCharacterCount();

      try {
        const previewEngine = await this.waitForPreviewEngine();
        this.THREE = previewEngine;
        this.SVGLoader = previewEngine.SVGLoader;
        this.svgLoader = new this.SVGLoader();
        this.opentype = previewEngine.opentype;
        if (!this.opentype || typeof this.opentype.parse !== 'function') {
          throw new Error('Font processor did not load');
        }

        const fontData = await this.waitForMonogramFontData();
        this.monogramFonts = [
          this.parseEmbeddedFont(fontData.leftFontBase64, 'left'),
          this.parseEmbeddedFont(fontData.centerFontBase64, 'center'),
          this.parseEmbeddedFont(fontData.rightFontBase64, 'right')
        ];
        this.setupScene();
        this.resizeRenderer();
        this.rebuildMonogram();
      } catch (error) {
        console.error('Circle monogram preview could not start:', error);
        const reason = error instanceof Error && error.message ? ` (${error.message})` : '';
        this.showError(`The circle monogram preview could not load${reason}. Please contact us or try again shortly.`);
      }
    }

    resolveProductForm() {
      const requestedId = this.dataset.productFormId;
      const requestedForm = requestedId ? document.getElementById(requestedId) : null;
      if (
        requestedForm instanceof HTMLFormElement &&
        requestedForm.matches('form[action*="/cart/add"]') &&
        requestedForm.closest('product-form')
      ) {
        return requestedForm;
      }

      const productScope = this.closest('product-info') || this.closest('.shopify-section') || document;
      return (
        productScope.querySelector('product-form form[action*="/cart/add"]') ||
        document.querySelector('product-form form[action*="/cart/add"]')
      );
    }

    waitForPreviewEngine() {
      if (window.CustomIcePreviewEngine) return Promise.resolve(window.CustomIcePreviewEngine);
      return new Promise((resolve, reject) => {
        let attempts = 0;
        const timer = window.setInterval(() => {
          attempts += 1;
          if (window.CustomIcePreviewEngine) {
            window.clearInterval(timer);
            resolve(window.CustomIcePreviewEngine);
            return;
          }
          if (attempts >= 100) {
            window.clearInterval(timer);
            reject(new Error('Preview engine did not load'));
          }
        }, 50);
      });
    }

    waitForMonogramFontData() {
      if (window.CircleMonogramFontData) return Promise.resolve(window.CircleMonogramFontData);
      return new Promise((resolve, reject) => {
        let attempts = 0;
        const timer = window.setInterval(() => {
          attempts += 1;
          if (window.CircleMonogramFontData) {
            window.clearInterval(timer);
            resolve(window.CircleMonogramFontData);
            return;
          }
          if (attempts >= 100) {
            window.clearInterval(timer);
            reject(new Error('embedded monogram fonts did not load'));
          }
        }, 50);
      });
    }

    parseEmbeddedFont(encoded, position) {
      if (typeof encoded !== 'string' || !encoded.length) {
        throw new Error(`${position} monogram font data is missing`);
      }
      const binary = window.atob(encoded);
      const bytes = new Uint8Array(binary.length);
      for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
      return this.opentype.parse(bytes.buffer);
    }

    bindEvents() {
      this.textInput.addEventListener('input', () => {
        const rawValue = this.textInput.value;
        const normalized = this.normalizeInitials(rawValue);
        if (rawValue !== normalized) this.textInput.value = normalized;
        this.invalidateAttachedPreview();
        this.updateCharacterCount();
        window.clearTimeout(this.updateTimer);
        this.updateTimer = window.setTimeout(() => this.rebuildMonogram(), 90);
      });

      this.textInput.addEventListener('blur', () => {
        this.textInput.value = this.normalizeInitials(this.textInput.value);
        this.updateCharacterCount();
        this.rebuildMonogram();
      });

      this.viewButtons.forEach((button) => {
        button.addEventListener('click', () => this.setView(button.dataset.iceView));
      });

      this.viewer.addEventListener('pointerdown', (event) => {
        if (event.target.closest('.ice-customizer__view-controls')) return;
        if (!this.currentVectorSvg || !this.sceneReady) return;
        if (this.cameraView === 'top') {
          this.cameraView = '3d';
          this.updateViewButtons();
          this.frameCamera();
        }
        this.dragStart = { x: event.clientX, y: event.clientY, yaw: this.yaw, pitch: this.pitch };
        this.viewer.setPointerCapture(event.pointerId);
      });

      this.viewer.addEventListener('pointermove', (event) => {
        if (!this.dragStart) return;
        this.yaw = this.dragStart.yaw + (event.clientX - this.dragStart.x) * 0.012;
        this.pitch = this.dragStart.pitch + (event.clientY - this.dragStart.y) * 0.012;
        this.updateModelRotation();
        this.render();
      });

      const endDrag = () => {
        this.dragStart = null;
      };
      this.viewer.addEventListener('pointerup', endDrag);
      this.viewer.addEventListener('pointercancel', endDrag);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.resizeRenderer());
        this.resizeObserver.observe(this.viewer);
      }

      this.documentSubmitHandler = (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!form.matches('form[action*="/cart/add"], form[id^="product-form-"]')) return;
        if (form !== this.productForm) return;
        this.handleProductSubmit(event, form);
      };
      document.addEventListener('submit', this.documentSubmitHandler, true);
    }

    normalizeInitials(value) {
      return [...String(value || '').toUpperCase()]
        .filter((character) => /[A-Z]/.test(character))
        .slice(0, 3)
        .join('');
    }

    updateCharacterCount() {
      if (!this.characterCount) return;
      this.characterCount.textContent = `${[...this.textInput.value].length} / 3`;
    }

    setupScene() {
      const THREE = this.THREE;
      this.renderer = new THREE.WebGLRenderer({
        canvas: this.canvas,
        antialias: true,
        alpha: false,
        preserveDrawingBuffer: true,
        powerPreference: 'high-performance'
      });
      this.renderer.setClearColor(0x000000, 1);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.18;
      this.renderer.shadowMap.enabled = false;

      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0x000000);
      this.camera = new THREE.PerspectiveCamera(31, 1, 1, 600);
      this.scene.add(new THREE.HemisphereLight(0xe8f9ff, 0x071018, 2.25));

      const key = new THREE.DirectionalLight(0xffffff, 4.3);
      key.position.set(-70, 125, 80);
      key.castShadow = false;
      this.scene.add(key);

      const rim = new THREE.DirectionalLight(0x8cdfff, 3.5);
      rim.position.set(90, 70, -85);
      this.scene.add(rim);

      const front = new THREE.PointLight(0xd9f7ff, 75, 280, 2);
      front.position.set(0, 45, 115);
      this.scene.add(front);

      this.model = new THREE.Group();
      this.model.rotation.order = 'YXZ';
      this.scene.add(this.model);

      const cubeGeometry = new THREE.BoxGeometry(50, 50, 50, 1, 1, 1);
      const cubeMaterial = new THREE.MeshPhysicalMaterial({
        color: 0xbcefff,
        transparent: true,
        opacity: 0.34,
        transmission: 0.6,
        thickness: 8,
        ior: 1.31,
        roughness: 0.12,
        metalness: 0,
        clearcoat: 0.35,
        clearcoatRoughness: 0.15,
        side: THREE.DoubleSide,
        depthWrite: false
      });
      this.cube = new THREE.Mesh(cubeGeometry, cubeMaterial);
      this.cube.castShadow = false;
      this.cube.receiveShadow = false;
      this.model.add(this.cube);

      const edgeMaterial = new THREE.LineBasicMaterial({
        color: 0xdff9ff,
        transparent: true,
        opacity: 0.42
      });
      this.cubeEdges = new THREE.LineSegments(new THREE.EdgesGeometry(cubeGeometry, 18), edgeMaterial);
      this.model.add(this.cubeEdges);

      this.updateModelRotation();
      this.sceneReady = true;
    }

    rebuildMonogram() {
      if (!this.sceneReady || !this.monogramFonts?.length) return;
      const initials = this.normalizeInitials(this.textInput.value);
      this.currentInitials = initials;
      if (this.enteredTextProperty) this.enteredTextProperty.value = initials;

      if (!initials) {
        this.clearPreview();
        return;
      }

      try {
        const glyphEntries = [];
        let cursorX = 0;
        [...initials].forEach((character, index) => {
          const font = this.monogramFonts[index];
          const glyph = font.charToGlyph(character);
          if (!glyph || glyph.index === 0) throw new Error(`The monogram font does not include ${character}.`);

          const path = glyph.getPath(0, 0, 100, { kerning: false }, font);
          const pathData = path.toPathData(3);
          if (!pathData || pathData.includes('NaN')) {
            throw new Error(`The ${character} outline could not be prepared.`);
          }
          const bounds = path.getBoundingBox();
          glyphEntries.push({ pathData, x: cursorX, bounds });
          const advance = ((glyph.advanceWidth || font.unitsPerEm * 0.325) / font.unitsPerEm) * 100;
          cursorX += advance;
        });

        const bounds = glyphEntries.reduce(
          (result, entry) => ({
            x1: Math.min(result.x1, entry.bounds.x1 + entry.x),
            y1: Math.min(result.y1, entry.bounds.y1),
            x2: Math.max(result.x2, entry.bounds.x2 + entry.x),
            y2: Math.max(result.y2, entry.bounds.y2)
          }),
          { x1: Infinity, y1: Infinity, x2: -Infinity, y2: -Infinity }
        );
        const width = bounds.x2 - bounds.x1;
        const height = bounds.y2 - bounds.y1;
        if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
          throw new Error('Enter three usable initials.');
        }

        this.currentVectorSvg =
          '<svg xmlns="http://www.w3.org/2000/svg">' +
          glyphEntries
            .map(
              (entry) =>
                `<path fill="#000000" fill-rule="evenodd" transform="translate(${entry.x.toFixed(3)} 0)" d="${entry.pathData}"/>`
            )
            .join('') +
          '</svg>';
        this.dragHint.hidden = false;
        this.viewControls.hidden = false;
        this.classList.add('has-logo');
        this.buildRelief();
      } catch (error) {
        console.error('Circle monogram conversion failed:', error);
        this.clearPreview(false);
        this.showError(error.message || 'The monogram could not be converted into a 3D preview.');
      }
    }

    clearPreview(clearError = true) {
      this.currentVectorSvg = '';
      this.disposeRelief();
      this.dragHint.hidden = true;
      this.viewControls.hidden = true;
      this.classList.remove('has-logo');
      if (clearError) this.hideError();
      this.render();
    }

    buildRelief() {
      if (!this.sceneReady || !this.currentVectorSvg) return;
      const THREE = this.THREE;
      const parsed = this.svgLoader.parse(this.currentVectorSvg);
      const shapes = [];
      let vertexCount = 0;

      parsed.paths.forEach((path) => {
        const style = (path.userData && path.userData.style) || {};
        if (style.fill === 'none' || Number(style.fillOpacity) === 0) return;
        this.SVGLoader.createShapes(path).forEach((shape) => shapes.push(shape));
      });
      if (!shapes.length) throw new Error('No usable monogram outlines were created.');

      const geometries = [];
      shapes.forEach((shape) => {
        const geometry = new THREE.ExtrudeGeometry(shape, {
          depth: 5,
          bevelEnabled: false,
          curveSegments: 28,
          steps: 1
        });
        vertexCount += geometry.getAttribute('position').count;
        if (vertexCount > 350000) {
          geometry.dispose();
          geometries.forEach((item) => item.dispose());
          throw new Error('This monogram is too complex for an instant preview.');
        }
        geometry.computeBoundingBox();
        geometries.push(geometry);
      });

      const bounds = new THREE.Box3();
      geometries.forEach((geometry) => bounds.union(geometry.boundingBox));
      const width = bounds.max.x - bounds.min.x;
      const height = bounds.max.y - bounds.min.y;
      const longest = Math.max(width, height);
      if (!Number.isFinite(longest) || longest <= 0) {
        geometries.forEach((geometry) => geometry.dispose());
        throw new Error('The monogram has no usable filled area.');
      }

      const scale = 45 / longest;
      const centerX = (bounds.min.x + bounds.max.x) / 2;
      const centerY = (bounds.min.y + bounds.max.y) / 2;
      const reliefMaterial = new THREE.MeshPhysicalMaterial({
        color: 0xdaf8ff,
        transparent: true,
        opacity: 0.92,
        transmission: 0.16,
        thickness: 1.5,
        ior: 1.31,
        roughness: 0.14,
        metalness: 0,
        clearcoat: 0.45,
        clearcoatRoughness: 0.14,
        side: THREE.DoubleSide,
        depthWrite: true
      });
      const nextRelief = new THREE.Group();

      geometries.forEach((geometry) => {
        geometry.translate(-centerX, -centerY, 0);
        geometry.scale(scale, -scale, 1);
        geometry.rotateX(-Math.PI / 2);
        geometry.translate(0, 25.03, 0);
        geometry.computeVertexNormals();
        const mesh = new THREE.Mesh(geometry, reliefMaterial);
        mesh.castShadow = false;
        mesh.receiveShadow = false;
        mesh.renderOrder = 3;
        nextRelief.add(mesh);
      });

      this.disposeRelief();
      this.relief = nextRelief;
      this.reliefMaterial = reliefMaterial;
      this.model.add(this.relief);
      this.hideError();
      this.updateModelRotation();
      this.invalidateAttachedPreview();
      this.render();
    }

    disposeRelief() {
      if (!this.relief || !this.model) return;
      this.model.remove(this.relief);
      this.relief.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
      });
      if (this.reliefMaterial) this.reliefMaterial.dispose();
      this.relief = null;
      this.reliefMaterial = null;
    }

    updateModelRotation() {
      if (!this.model) return;
      this.model.rotation.y = this.yaw;
      this.model.rotation.x = this.pitch;
    }

    setView(view) {
      if (!this.camera || !this.model) return;
      if (view === 'top') {
        this.cameraView = 'top';
        this.yaw = 0;
        this.pitch = 0;
      } else {
        this.cameraView = '3d';
        this.yaw = Math.PI / 4;
        this.pitch = -0.08;
      }
      this.updateModelRotation();
      this.updateViewButtons();
      this.frameCamera();
      this.render();
    }

    updateViewButtons() {
      this.viewButtons.forEach((button) => {
        const active = button.dataset.iceView === this.cameraView;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    resizeRenderer() {
      if (!this.renderer || !this.camera) return;
      const rect = this.viewer.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      this.renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      this.frameCamera();
      this.camera.updateProjectionMatrix();
      this.render();
    }

    frameCamera() {
      if (!this.camera || !this.THREE) return;
      const THREE = this.THREE;
      const verticalFov = THREE.MathUtils.degToRad(this.camera.fov);
      const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * this.camera.aspect);
      const limitingFov = Math.min(verticalFov, horizontalFov);
      const distance = (50 / Math.sin(limitingFov / 2)) * 1.16;
      const target = new THREE.Vector3(0, 1, 0);
      const direction =
        this.cameraView === 'top'
          ? new THREE.Vector3(0, 1, 0)
          : new THREE.Vector3(1.05, 0.92, 1.35).normalize();

      this.camera.up.set(0, this.cameraView === 'top' ? 0 : 1, this.cameraView === 'top' ? -1 : 0);
      this.camera.position.copy(target).addScaledVector(direction, distance);
      this.camera.lookAt(target);
    }

    render() {
      if (this.renderer && this.scene && this.camera) this.renderer.render(this.scene, this.camera);
    }

    handleProductSubmit(event, form) {
      if (this.allowPreviewSubmit && this.submittingForm === form) {
        this.allowPreviewSubmit = false;
        this.submittingForm = null;
        return;
      }

      this.textInput.value = this.normalizeInitials(this.textInput.value);
      this.rebuildMonogram();
      if (this.currentInitials.length !== 3) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError('Enter exactly 3 initials before adding this item to the cart.');
        this.textInput.focus();
        return;
      }
      if (!this.currentVectorSvg || !this.relief) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError('Finish the monogram preview before adding this item to the cart.');
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      const submitter = event.submitter;
      this.attachTopPreview(form).then((attached) => {
        if (!attached) return;
        this.allowPreviewSubmit = true;
        this.submittingForm = form;
        if (form.requestSubmit) {
          submitter && submitter.isConnected ? form.requestSubmit(submitter) : form.requestSubmit();
        } else {
          form.submit();
        }
      });
    }

    attachTopPreview(form) {
      if (!this.previewFileInput || typeof DataTransfer === 'undefined') {
        this.showError('This browser cannot attach the top-view preview. Please use a current browser.');
        return Promise.resolve(false);
      }

      const previous = { cameraView: this.cameraView, yaw: this.yaw, pitch: this.pitch };
      this.cameraView = 'top';
      this.yaw = 0;
      this.pitch = 0;
      this.updateModelRotation();
      this.updateViewButtons();
      this.frameCamera();
      this.render();

      return new Promise((resolve) => {
        this.canvas.toBlob((blob) => {
          this.cameraView = previous.cameraView;
          this.yaw = previous.yaw;
          this.pitch = previous.pitch;
          this.updateModelRotation();
          this.updateViewButtons();
          this.frameCamera();
          this.render();

          if (!blob) {
            this.showError('The top-view preview could not be created. Please try again.');
            resolve(false);
            return;
          }

          const transfer = new DataTransfer();
          transfer.items.add(
            new File([blob], `circle-monogram-${this.currentInitials}-top-view-${Date.now()}.png`, {
              type: 'image/png'
            })
          );
          this.previewFileInput.files = transfer.files;
          this.previewFileInput.removeAttribute('form');
          if (!form.contains(this.previewFileInput)) form.appendChild(this.previewFileInput);
          const submittedPreview = new FormData(form).get('properties[Top-view preview]');
          const valid = submittedPreview instanceof File && submittedPreview.size > 0;
          if (valid) {
            if (this.previewAttachedProperty) this.previewAttachedProperty.value = 'Yes';
            this.hideError();
          } else {
            this.previewFileInput.value = '';
            this.showError('The top-view preview could not be attached. Please try again.');
          }
          resolve(valid);
        }, 'image/png');
      });
    }

    invalidateAttachedPreview() {
      if (this.previewFileInput?.files?.length) this.previewFileInput.value = '';
      if (this.previewAttachedProperty) this.previewAttachedProperty.value = 'No';
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
    }

    hideError() {
      this.error.textContent = '';
      this.error.hidden = true;
    }

    disconnectedCallback() {
      window.clearTimeout(this.updateTimer);
      if (this.documentSubmitHandler) document.removeEventListener('submit', this.documentSubmitHandler, true);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.disposeRelief();
      if (this.renderer) this.renderer.dispose();
    }
  }

  customElements.define('etsy-circle-monogram-customizer', CircleMonogramCustomizer);
}
